import { AfterViewInit, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { CatalogCrudModalComponent } from '../shared/components/catalog-crud-modal';
import { CatalogDeleteConfirmModalComponent } from '../shared/components/catalog-delete-confirm-modal';
import { CatalogPageCardComponent } from '../shared/components/catalog-page-card';
import { CatalogTableToolbarComponent } from '../shared/components/catalog-table-toolbar';
import { CatalogDatatableController, DatatableViewStateSnapshot } from '../shared/catalog-datatable.controller';
import { CatalogModalController } from '../shared/catalog-modal.controller';
import { NonNamedDealerService } from './non-named-dealer.service';
import { NonNamedDealer, UpsertNonNamedDealerRequest } from './non-named-dealer.types';

interface NonNamedDealerFormValue {
  name: string;
}

type NonNamedDealerFormControlName = keyof NonNamedDealerFormValue;

@Component({
  selector: 'app-non-named-dealer',
  imports: [
    ReactiveFormsModule,
    CatalogPageCardComponent,
    CatalogTableToolbarComponent,
    CatalogCrudModalComponent,
    CatalogDeleteConfirmModalComponent,
  ],
  templateUrl: './non-named-dealer.html',
  styleUrl: './non-named-dealer.css',
})
export class NonNamedDealerComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly nameMaxLength = 200;
  private readonly datatableId = 'kt_non_named_dealer_datatable';
  private readonly createModalId = 'create_non_named_dealer_modal';
  private readonly editModalId = 'edit_non_named_dealer_modal';
  private readonly deleteConfirmModalId = 'delete_non_named_dealer_confirm_modal';
  private readonly formBuilder = inject(FormBuilder);
  private readonly datatableController = new CatalogDatatableController(this.datatableId);
  private readonly modalController = new CatalogModalController();

  private createModalObserver?: MutationObserver;
  private editModalObserver?: MutationObserver;

  protected readonly createForm = this.formBuilder.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(this.nameMaxLength)]],
  });
  protected readonly editForm = this.formBuilder.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(this.nameMaxLength)]],
  });
  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly rows = signal<NonNamedDealer[]>([]);
  protected readonly isCreating = signal(false);
  protected readonly createFormSubmitted = signal(false);
  protected readonly createFormError = signal('');
  protected readonly editingId = signal<number | null>(null);
  protected readonly isUpdating = signal(false);
  protected readonly editFormSubmitted = signal(false);
  protected readonly editFormError = signal('');
  protected readonly deletingId = signal<number | null>(null);
  protected readonly pendingDeleteId = signal<number | null>(null);
  protected readonly alertVariant = signal<'success' | 'destructive' | null>(null);
  protected readonly alertTitle = signal('');
  protected readonly alertMessage = signal('');
  protected readonly hasAlert = computed(() => this.alertVariant() !== null);

  constructor(private readonly nonNamedDealerService: NonNamedDealerService) {}

  ngOnInit(): void {
    this.loadRows();
  }

  ngAfterViewInit(): void {
    this.bindCreateModalCloseReset();
    this.bindEditModalCloseReset();
  }

  ngOnDestroy(): void {
    this.createModalObserver?.disconnect();
    this.editModalObserver?.disconnect();
  }

  protected shouldShowCreateFieldError(controlName: NonNamedDealerFormControlName): boolean {
    return this.createFormSubmitted() && this.createForm.controls[controlName].invalid;
  }

  protected shouldShowEditFieldError(controlName: NonNamedDealerFormControlName): boolean {
    return this.editFormSubmitted() && this.editForm.controls[controlName].invalid;
  }

  protected shouldShowCreateRequiredError(controlName: NonNamedDealerFormControlName): boolean {
    const control = this.createForm.controls[controlName];
    return this.createFormSubmitted() && !!control.errors?.['required'];
  }

  protected shouldShowCreateMaxLengthError(controlName: NonNamedDealerFormControlName): boolean {
    const control = this.createForm.controls[controlName];
    return this.createFormSubmitted() && !!control.errors?.['maxlength'];
  }

  protected shouldShowEditRequiredError(controlName: NonNamedDealerFormControlName): boolean {
    const control = this.editForm.controls[controlName];
    return this.editFormSubmitted() && !!control.errors?.['required'];
  }

  protected shouldShowEditMaxLengthError(controlName: NonNamedDealerFormControlName): boolean {
    const control = this.editForm.controls[controlName];
    return this.editFormSubmitted() && !!control.errors?.['maxlength'];
  }

  protected loadRows(snapshot?: DatatableViewStateSnapshot): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.nonNamedDealerService.getNonNamedDealers().subscribe({
      next: (rows) => {
        this.rows.set(rows);
        this.isLoading.set(false);
        this.datatableController.initialize(snapshot);
      },
      error: () => {
        this.errorMessage.set('Unable to load non named dealer data from the API.');
        this.isLoading.set(false);
      },
    });
  }

  protected onCreate(): void {
    this.createFormSubmitted.set(true);
    this.createFormError.set('');

    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }

    const payload = this.toPayload(this.createForm.getRawValue());

    this.isCreating.set(true);

    this.nonNamedDealerService.createNonNamedDealer(payload).subscribe({
      next: () => {
        this.isCreating.set(false);
        this.closeCreateModal();
        this.resetCreateForm();
        window.KTToast?.show({
          message: 'Non named dealer created successfully.',
          variant: 'success',
        });
        this.loadRows();
      },
      error: () => {
        this.isCreating.set(false);
        this.createFormError.set('Unable to create non named dealer at this time. Please try again.');
      },
    });
  }

  protected resetCreateForm(): void {
    this.createForm.reset(this.getEmptyFormValue());
    this.createFormSubmitted.set(false);
    this.createFormError.set('');
  }

  protected openEditModal(row: NonNamedDealer): void {
    this.editingId.set(row.id);
    this.editForm.reset({
      name: row.name,
    });
    this.editFormSubmitted.set(false);
    this.editFormError.set('');
    this.modalController.open(this.editModalId);
  }

  protected onUpdate(): void {
    this.editFormSubmitted.set(true);
    this.editFormError.set('');

    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    const id = this.editingId();
    if (id === null) {
      this.editFormError.set('Unable to identify the non named dealer to update.');
      return;
    }

    const payload = this.toPayload(this.editForm.getRawValue());
    const datatableSnapshot = this.datatableController.captureViewState();

    this.isUpdating.set(true);

    this.nonNamedDealerService.updateNonNamedDealer(id, payload).subscribe({
      next: () => {
        this.isUpdating.set(false);
        this.closeEditModal();
        this.resetEditForm();
        window.KTToast?.show({
          message: 'Non named dealer updated successfully.',
          variant: 'success',
        });
        this.loadRows(datatableSnapshot);
      },
      error: () => {
        this.isUpdating.set(false);
        this.editFormError.set('Unable to update non named dealer at this time. Please try again.');
      },
    });
  }

  protected resetEditForm(): void {
    this.editForm.reset(this.getEmptyFormValue());
    this.editingId.set(null);
    this.editFormSubmitted.set(false);
    this.editFormError.set('');
  }

  protected onTableActionClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const editButton = target?.closest('button[data-action="edit-non-named-dealer"]') as HTMLButtonElement | null;
    const deleteButton = target?.closest('button[data-action="delete-non-named-dealer"]') as HTMLButtonElement | null;

    if (editButton && !editButton.disabled) {
      const rowId = this.getRowIdFromButton(editButton);
      if (rowId === null) {
        return;
      }

      const row = this.rows().find((item) => item.id === rowId);
      if (!row) {
        return;
      }

      this.openEditModal(row);
      return;
    }

    if (!deleteButton || deleteButton.disabled) {
      return;
    }

    const rowId = this.getRowIdFromButton(deleteButton);
    if (rowId === null) {
      return;
    }

    this.openDeleteConfirmationModal(rowId);
  }

  protected cancelDelete(): void {
    this.pendingDeleteId.set(null);
    this.closeDeleteConfirmationModal();
  }

  protected confirmDelete(): void {
    const rowId = this.pendingDeleteId();

    if (rowId === null || this.deletingId() !== null) {
      return;
    }

    this.closeDeleteConfirmationModal();
    this.pendingDeleteId.set(null);
    this.removeRow(rowId);
  }

  protected removeRow(rowId: number): void {
    if (this.deletingId() !== null) {
      return;
    }

    this.deletingId.set(rowId);

    this.nonNamedDealerService.deleteNonNamedDealer(rowId).subscribe({
      next: (result) => {
        this.deletingId.set(null);

        if (result.kind === 'success') {
          const snapshot = this.datatableController.captureViewState();
          this.rows.update((rows) => rows.filter((item) => item.id !== rowId));
          this.showSuccessAlert('Non named dealer deleted successfully.');
          this.datatableController.initialize(snapshot);
          return;
        }

        this.showDestructiveAlert(
          'Unable to delete non named dealer.',
          result.message,
        );
      },
      error: () => {
        this.deletingId.set(null);
        this.showDestructiveAlert(
          'Unable to delete non named dealer.',
          'Unable to delete non named dealer at this time. Please try again.',
        );
      },
    });
  }

  protected dismissAlert(): void {
    this.alertVariant.set(null);
    this.alertTitle.set('');
    this.alertMessage.set('');
  }

  private bindCreateModalCloseReset(): void {
    this.createModalObserver = this.modalController.observeClose(this.createModalId, () => {
      this.resetCreateForm();
    });
  }

  private bindEditModalCloseReset(): void {
    this.editModalObserver = this.modalController.observeClose(this.editModalId, () => {
      this.resetEditForm();
    });
  }

  private closeCreateModal(): void {
    this.modalController.close(this.createModalId);
  }

  private closeEditModal(): void {
    this.modalController.close(this.editModalId);
  }

  private openDeleteConfirmationModal(rowId: number): void {
    this.pendingDeleteId.set(rowId);
    this.modalController.open(this.deleteConfirmModalId);
  }

  private closeDeleteConfirmationModal(): void {
    this.modalController.close(this.deleteConfirmModalId);
  }

  private showSuccessAlert(message: string): void {
    this.alertVariant.set('success');
    this.alertTitle.set(message);
    this.alertMessage.set('');
  }

  private showDestructiveAlert(title: string, message: string): void {
    this.alertVariant.set('destructive');
    this.alertTitle.set(title);
    this.alertMessage.set(message);
  }

  private getRowIdFromButton(button: HTMLButtonElement): number | null {
    const rowIdRaw = button.getAttribute('data-non-named-dealer-id');
    const rowId = rowIdRaw ? Number(rowIdRaw) : Number.NaN;

    return Number.isFinite(rowId) ? rowId : null;
  }

  private getEmptyFormValue(): NonNamedDealerFormValue {
    return {
      name: '',
    };
  }

  private toPayload(rawValue: NonNamedDealerFormValue): UpsertNonNamedDealerRequest {
    return {
      name: rawValue.name.trim(),
    };
  }
}
